import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownIcon, ArrowRightIcon, CheckCircle2Icon, FileTextIcon, InfoIcon, ListIcon, UserIcon } from 'lucide-react';
import { trustExample as ex, type Bi } from '../../data/trustSolution';
type Pick = (b: Bi) => string;
const CONTROL_ICONS = {
  answer: ListIcon,
  sources: FileTextIcon,
  why: InfoIcon
} as const;
function Connector() {
  return <div className="flex items-center justify-center py-1 xl:py-0" aria-hidden="true" data-id="element-6960" data-mp="82pgkqov4ypj">
      <ArrowRightIcon className="hidden xl:block w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} data-id="element-6961" data-mp="c4kfxpn8h20o" />
      <ArrowDownIcon className="xl:hidden w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} data-id="element-6962" data-mp="3uafas4k48hjg" />
    </div>;
}
export function TrustExample({
  pick
}: {
  pick: Pick;
}) {
  const r = ex.recommendation;
  // data-note: {ex.demoNote}
  return <div data-id="element-6963" data-mp="1sisryqadnsy2">
      <div className="rounded-2xl border border-[#EEEFF4] bg-[#FCFCFE] p-4 md:p-5 grid grid-cols-1 xl:grid-cols-[0.8fr_auto_1.55fr_auto_0.85fr] gap-3 xl:gap-4 items-stretch" data-id="element-6964" data-mp="1sk3vcozwuyon">
        {/* Question */}
        <div className="rounded-xl border border-[#EEEFF4] bg-white p-4 flex flex-col" data-id="element-6965" data-mp="1c680te1sowko">
          <p className="flex items-center gap-2 font-sans text-[12.5px] font-semibold text-[#121A3A]" data-id="element-6966" data-mp="178y91rqxo8to">
            <UserIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-6967" data-mp="1kyib0vcox21x" />
            {pick(ex.question.label)}
          </p>
          <div className="mt-3 flex-1 rounded-xl bg-[#F4F5FA] px-4 py-3" data-id="element-6968" data-mp="1h0c6zv38l0kp">
            <p className="font-sans text-[14.5px] leading-snug text-[#121A3A]" data-id="element-6969" data-mp="t8x8yeiht5f9">{pick(ex.question.text)}</p>
            <p className="mt-2 text-right font-sans text-[10.5px] text-[#9AA0B4]" data-id="element-6970" data-mp="ob582uzve8yx">{pick(ex.question.time)}</p>
          </div>
        </div>

        <Connector data-id="element-6971" data-mp="xa8ry16v2seh" />

        {/* Verified recommendation */}
        <div className="rounded-xl border border-[#EEEFF4] bg-white p-4 md:p-5 shadow-[0_10px_30px_-24px_rgba(18,26,58,0.35)]" data-id="element-6972" data-mp="x8o0ikfufpku">
          <p className="font-sans text-[13.5px] font-semibold text-[#121A3A]" data-id="element-6973" data-mp="28p5t29gbl827">{pick(r.label)}</p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-[1.25fr_1fr] gap-3 md:gap-5 items-center" data-id="element-6974" data-mp="16bq4vuz3i7xd">
            <p className="flex items-center gap-3 rounded-lg bg-[#EAF6EF] px-4 py-3" data-id="element-6975" data-mp="1baklcq9lbkeb">
              <CheckCircle2Icon className="w-7 h-7 shrink-0 text-white fill-[#22A06B]" aria-hidden="true" data-id="element-6976" data-mp="1fcekgu450gcd" />
              <span className="font-sans text-[15px] font-medium text-[#1C7A52] whitespace-nowrap" data-id="element-6977" data-mp="1kb90joapzqss">
                {pick(r.confidence)} · <span className="font-semibold text-[#121A3A]" data-id="element-6978" data-mp="nm1emf66vfoc">{pick(r.score)}</span>
              </span>
              <InfoIcon className="ml-auto w-4 h-4 text-[#4A5268]" aria-hidden="true" data-id="element-6979" data-mp="rnvbecd9jzj8" />
            </p>
            <ul className="space-y-1" data-id="element-6980" data-mp="wkvh2m67b09t">
              {r.checks.map(c => <li key={c.fr} className="flex items-center gap-2 font-sans text-[12.5px] text-[#3A4260]" data-id="element-6981" data-mp="11d6ixuki4w50">
                  <CheckCircle2Icon className="w-3.5 h-3.5 shrink-0 text-white fill-[#22A06B]" aria-hidden="true" data-id="element-6982" data-mp="3y0yjusy2ayz" />
                  {pick(c)}
                </li>)}
            </ul>
          </div>
          <p className="mt-3 font-sans text-[14px] leading-relaxed text-[#4A5268]" data-id="element-6983" data-mp="1brxkj4rfr47">{pick(r.answer)}</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-6984" data-mp="3skfiwz303ebr">
            {r.controls.map(ctl => {
            const Icon = CONTROL_ICONS[ctl.key as keyof typeof CONTROL_ICONS];
            return <span key={ctl.key} className="inline-flex items-center gap-2 rounded-lg border border-[#E4E6EE] bg-white px-3.5 py-1.5 font-sans text-[12.5px] text-[#121A3A]" data-id="element-6985" data-mp="3jho36n33dweb">
                  <Icon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-6986" data-mp="39qjqxv4swpn6" />
                  {pick(ctl.label)}
                </span>;
          })}
          </div>
        </div>

        <Connector data-id="element-6987" data-mp="2vsvftf0jcwvb" />

        {/* Next action */}
        <div className="rounded-xl border border-[#EEEFF4] bg-white p-4 flex flex-col" data-id="element-6988" data-mp="2vrk1wmddm8qh">
          <div className="flex items-start gap-3" data-id="element-6989" data-mp="2qutlfxxntb4j">
            <FileTextIcon className="w-7 h-7 shrink-0 text-[#4F46E5]" strokeWidth={1.5} aria-hidden="true" data-id="element-6990" data-mp="2qw4rk4ekuuyt" />
            <div data-id="element-6991" data-mp="ys7o6izx0czv">
              <p className="font-sans text-[12px] font-semibold text-[#121A3A]" data-id="element-6992" data-mp="ysqwd7zrjn0p">{pick(ex.action.label)}</p>
              <p className="mt-2 font-sans text-[15px] font-semibold leading-snug text-[#121A3A]" data-id="element-6993" data-mp="l1m3qputbuzv">{pick(ex.action.title)}</p>
              <p className="mt-1.5 font-sans text-[12.5px] leading-snug text-[#6B7288]" data-id="element-6994" data-mp="pyck7eaj4slt">{pick(ex.action.text)}</p>
            </div>
          </div>
          <Link to="/permis" className="mt-4 xl:mt-auto inline-flex items-center justify-center gap-2 rounded-md bg-[#4F46E5] px-4 py-2.5 font-sans text-[13.5px] font-medium text-white hover:bg-[#4338CA] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4F46E5] focus-visible:ring-offset-2" data-id="element-6995" data-mp="g1q0wtxuq9lw">
            {pick(ex.action.button)}
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-6996" data-mp="21esqoi43vywj" />
          </Link>
        </div>
      </div>

      <p className="mt-3 text-center font-sans text-[12.5px] text-[#4A5268]" data-id="element-6997" data-mp="2bihn0z19j8qf">
        {pick(ex.trace)}
        <span className="hidden md:inline text-[#9AA0B4]" data-id="element-6998" data-mp="26kpin5jflu5c"> &nbsp;·&nbsp; {pick(ex.traceChain)}</span>
      </p>
    </div>;
}